import { useEffect, useState } from "react";

import dayjs from "dayjs";

import { FaDatabase } from "react-icons/fa";
import Col from "react-bootstrap/Col";

import { Option } from "@/@types/generics/Option";
import { GrievanceNotification } from "@/@types/sintegrada/GrievanceNotification";
import { useLayout } from "@/../../src/_metronic/layout/core";

import { useCurrentAccount } from "@/hooks/useCurrentAccount";
import { useDebounce } from "@/hooks/useDebounce";
import { RiskClassification } from "@/@types/sintegrada/RiskClassification";
import { Paginated } from "@/@types/generics/paginated";
import {
	getGrievanceNotificationItems,
	getGroupedByGrievanceNotification,
	GrievanceNotificationItemData,
} from "@/services/sintegrada/grievanceNotificationItem/getGrievanceNotificationItems";
import { formatIdentifier } from "@/utils/formatIdentifier";
import { exportExcel } from "@/utils/exports";
import { GenericObject } from "@/@types/generics/genericals";
import { ResumeGrouped } from "@/services/esus/attendanceService";
import { addressConcat } from "@/utils/adreessConcat";

import { GrievanceNotificationIndividualTable } from "./GrievanceNotificationIndividualTable";
import { GrievanceNotificationFilters } from "./GrievanceNotificationFilters";
import { PaginationLinks } from "@/components/PaginationLinks";
import { NoRecordsFeedback } from "@/components/NoRecordsFeedback";
import { SearchInput } from "@/components/SearchInput";
import { LoadingScreen } from "@/components/LoadingScreen";
import { GrievanceNotificationTable } from "./GrievanceNotificationTable";
import { ReportBaseLayout } from "@/components/sintegrada/ReportBaseLayout";

export function GrievanceNotificationReport() {
	const { setTitle } = useLayout();
	const { uf, ibgeCode, cnes } = useCurrentAccount();
	const [startDate, setStartDate] = useState(
		dayjs().add(-1, "month").startOf("day").format("YYYY-MM-DDTHH:mm")
	);
	const [endDate, setEndDate] = useState(dayjs().endOf("day").format("YYYY-MM-DDTHH:mm"));
	const [youngerAge, setYoungerAge] = useState<string>("");
	const [olderAge, setOlderAge] = useState<string>("");
	const [search, setSearch] = useState("");
	const debouncedSearch = useDebounce(search, 2000);
	const [selectedOptionGender, setSelectedOptionGender] = useState<Option<string>>();
	const [selectedOptionGrievanceNotification, setSelectedOptionGrievanceNotification] =
		useState<Option<GrievanceNotification>>();
	const [selectedOptionRiskClassification, setSelectedOptionRiskClassification] =
		useState<Option<RiskClassification>>();
	const [isCheckedIndividualReport, setIsCheckedIndividualReport] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [isLoadingGroupedGrievanceNotification, setIsLoadingGroupedGrievanceNotification] =
		useState(false);
	const [showLoadingScreen, setShowLoadingScreen] = useState(false);
	const [grievanceNotificationItems, setGrievanceNotificationItems] =
		useState<Paginated<GrievanceNotificationItemData>>();
	const [groupedByGrievanceNotificationItem, setGroupedByGrievanceNotificationItem] = useState<
		ResumeGrouped<GrievanceNotification, object>[]
	>([]);
	const [pageNumber, setPageNumber] = useState(1);
	const [filterVersion, setFilterVersion] = useState(0);

	function handleCheckboxChange() {
		setIsCheckedIndividualReport((prev) => !prev);
	}

	async function handleGrievanceNotificationItems(pageSize?: number) {
		return await getGrievanceNotificationItems({
			uf,
			ibgeCode,
			cnes,
			startDate: dayjs(startDate)
				.tz(Intl.DateTimeFormat().resolvedOptions().timeZone)
				.startOf("minute")
				.format("YYYY-MM-DDTHH:mm:ssZ"),
			endDate: dayjs(endDate)
				.tz(Intl.DateTimeFormat().resolvedOptions().timeZone)
				.endOf("minute")
				.format("YYYY-MM-DDTHH:mm:ssZ"),
			grievanceNotificationId: selectedOptionGrievanceNotification?.value?.id,
			riskClassificationId: selectedOptionRiskClassification?.value?.id,
			gender: selectedOptionGender?.value,
			youngerAge: youngerAge,
			olderAge: olderAge,
			search: search,
			pageNumber,
			pageSize,
		});
	}

	async function handleGroupedByGrievanceNotification(pageSize?: number) {
		return await getGroupedByGrievanceNotification({
			uf,
			ibgeCode,
			cnes,
			startDate: dayjs(startDate)
				.tz(Intl.DateTimeFormat().resolvedOptions().timeZone)
				.startOf("minute")
				.format("YYYY-MM-DDTHH:mm:ssZ"),
			endDate: dayjs(endDate)
				.tz(Intl.DateTimeFormat().resolvedOptions().timeZone)
				.endOf("minute")
				.format("YYYY-MM-DDTHH:mm:ssZ"),
			grievanceNotificationId: selectedOptionGrievanceNotification?.value?.id,
			riskClassificationId: selectedOptionRiskClassification?.value?.id,
			gender: selectedOptionGender?.value,
			youngerAge: youngerAge,
			olderAge: olderAge,
			search: search,
			pageNumber,
			pageSize,
		});
	}

	async function fetch() {
		setIsLoading(true);
		setGrievanceNotificationItems(await handleGrievanceNotificationItems());
		setIsLoading(false);
	}

	async function fetchGroupedByGrievanceNotification() {
		setIsLoadingGroupedGrievanceNotification(true);
		setGroupedByGrievanceNotificationItem(await handleGroupedByGrievanceNotification());
		setIsLoadingGroupedGrievanceNotification(false);
	}

	async function handleExportTable() {
		setShowLoadingScreen(true);
		const response = await handleGrievanceNotificationItems(
			grievanceNotificationItems?.totalRecords
		);
		setShowLoadingScreen(false);

		if (Object.keys(response?.data || []).length) {
			const excelData: GenericObject[] = transformToExportExcel(response?.data);
			exportExcel(excelData, `Tabela`);
		}
	}

	function transformToExportExcel(grievancesNotificationItems: GrievanceNotificationItemData[]) {
		return grievancesNotificationItems.map(
			(grievanceNotificationItem) =>
				({
					"Dt. da Notificação": dayjs(grievanceNotificationItem.attendanceDate).format(
						"DD/MM/YYYY HH:mm"
					),
					Nome: grievanceNotificationItem.patient.name,
					Cpf: formatIdentifier(grievanceNotificationItem.patient.cpf),
					"Data de Nascimento": dayjs(grievanceNotificationItem.patient.birthdate).format(
						"DD/MM/YYYY"
					),
					Sexo: grievanceNotificationItem.patient.gender,
					Endereço: addressConcat(
						grievanceNotificationItem.patient.street,
						grievanceNotificationItem.patient.houseNumber,
						grievanceNotificationItem.patient.neighborhoodText,
						grievanceNotificationItem.patient.uf,
						grievanceNotificationItem.patient.zipCode
					),
					"1º Sintoma": grievanceNotificationItem.firstSymptomAt
						? dayjs(grievanceNotificationItem.firstSymptomAt).format("DD-MM-YYYY")
						: "N/A",
					Classificação: grievanceNotificationItem.riskClassification.name,
					Notificação: grievanceNotificationItem.notification.name,
				} as GenericObject)
		);
	}

	async function handleExportGroupedByNotificationTable() {
		setShowLoadingScreen(true);
		const data = await handleGroupedByGrievanceNotification();
		setShowLoadingScreen(false);

		if (Object.keys(data || []).length) {
			const excelData: GenericObject[] = transformGroupedByNotificationToExportExcel(data);
			exportExcel(excelData, `Tabela`);
		}
	}

	function exportCheckedTable() {
		if (isCheckedIndividualReport) {
			handleExportTable();
		} else {
			handleExportGroupedByNotificationTable();
		}
	}

	function transformGroupedByNotificationToExportExcel(
		groupedGrievanceNotifications: ResumeGrouped<GrievanceNotification, object>[]
	) {
		return groupedGrievanceNotifications.map(
			(grievanceNotificationItem) =>
				({
					"Dt. da Notificação": `${dayjs(startDate).format("DD/MM/YYYY HH:mm")} - ${dayjs(
						endDate
					).format("DD/MM/YYYY HH:mm")}`,
					Nome: grievanceNotificationItem.key.name,
					Sexo: selectedOptionGender?.label ?? "Todos",
					"Faixa etária":
						youngerAge && olderAge ? `${youngerAge} - ${olderAge}` : "Todas",
					Classificação: selectedOptionRiskClassification?.label ?? "Todas",
					Total: grievanceNotificationItem.total,
				} as GenericObject)
		);
	}

	useEffect(() => {
		if (pageNumber !== 1) {
			setPageNumber(1);
			return;
		}
		fetch();

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [debouncedSearch]);

	useEffect(() => {
		if (isCheckedIndividualReport) {
			return;
		}
		fetchGroupedByGrievanceNotification();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isCheckedIndividualReport]);

	useEffect(() => {
		fetch();
		setTitle("Notificações de agravos");
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pageNumber, filterVersion]);

	return (
		<ReportBaseLayout>
			<LoadingScreen loading={showLoadingScreen} />

			<GrievanceNotificationFilters
				startDate={startDate}
				endDate={endDate}
				setStartDate={setStartDate}
				setEndDate={setEndDate}
				olderAge={olderAge}
				setOlderAge={setOlderAge}
				youngerAge={youngerAge}
				setYoungerAge={setYoungerAge}
				gender={selectedOptionGender}
				setSelectedOptionGender={setSelectedOptionGender}
				selectedGrievanceNotification={selectedOptionGrievanceNotification}
				setSelectedGrievanceNotification={setSelectedOptionGrievanceNotification}
				selectedOptionRiskClassification={selectedOptionRiskClassification}
				setSelectedOptionRiskClassification={setSelectedOptionRiskClassification}
				isCheckedIndividualReport={isCheckedIndividualReport}
				handleCheckboxChange={handleCheckboxChange}
				handleExport={exportCheckedTable}
				handleFilter={() => {
					setPageNumber(1);
					setFilterVersion((prev) => prev + 1);
					if (!isCheckedIndividualReport) {
						fetchGroupedByGrievanceNotification();
					}
				}}
			/>

			{isCheckedIndividualReport ? (
				<>
					<Col lg={12} className="d-flex justify-content-end mb-8">
						<SearchInput
							placeholder="Buscar por nome ou dt. nasc"
							value={search}
							setValue={setSearch}
						/>
					</Col>

					<GrievanceNotificationIndividualTable
						isLoading={isLoading}
						grievanceNotificationItemData={grievanceNotificationItems?.data || []}
					/>

					{grievanceNotificationItems?.totalRecords ? (
						<PaginationLinks
							itemsPerPage={grievanceNotificationItems.pageSize}
							totalPages={grievanceNotificationItems.totalRecords}
							changeSelectedPage={(value) => setPageNumber(value)}
							pageNumber={pageNumber || 0}
						/>
					) : (
						!isLoading && (
							<NoRecordsFeedback
								message="Nenhum resultado encontrado."
								icon={<FaDatabase />}
							/>
						)
					)}
				</>
			) : (
				<>
					<GrievanceNotificationTable
						groupedByGrievanceNotification={groupedByGrievanceNotificationItem}
						isLoading={isLoadingGroupedGrievanceNotification}
					/>

					{!isLoadingGroupedGrievanceNotification &&
						groupedByGrievanceNotificationItem.length === 0 && (
							<NoRecordsFeedback
								message="Nenhum resultado encontrado."
								icon={<FaDatabase />}
							/>
						)}
				</>
			)}
		</ReportBaseLayout>
	);
}
